/**
 * A browser history that hides the locale segment from the router.
 *
 * The site serves the same route tree in three languages — `/about`,
 * `/en/about`, `/es/about` — but duplicating every route definition three
 * times would also mean touching every `<Link to="/about">` in the app so it
 * knows which locale it is currently in. Instead the locale is translated at
 * the history boundary, which is the one place every URL already passes
 * through:
 *
 *  - `parseLocation` strips the locale segment, so the router only ever sees
 *    (and matches) the language-free path — every existing route definition,
 *    `useParams` call and `<Link to>` keeps working untouched.
 *  - `createHref` puts the active locale back on, so every link the router
 *    renders and every `pushState` it performs stays inside the visitor's
 *    language automatically.
 *
 * Switching language is therefore a URL rewrite in place, not a navigation:
 * the router's own path never changed, so the current route, its params and
 * its search are preserved by construction.
 */
import { createBrowserHistory, type RouterHistory } from '@tanstack/react-router'
import type { HistoryLocation } from '@tanstack/react-router'
import { type SupportedLangCode } from './language'
import {
  DEFAULT_LOCALE,
  isLocalizablePath,
  localizedHref,
  parseLocalePath,
  redirectedDefaultLocalePath,
} from './locale-path'

type HistoryState = HistoryLocation['state']

/** The router keys its history entries by index; a location needs one. */
function isHistoryState(value: unknown): value is HistoryState {
  return typeof value === 'object' && value !== null && '__TSR_index' in value
}

/**
 * Local re-implementation of the history package's `parseHref`, which it does
 * not re-export through `@tanstack/react-router`. Splits an href into the
 * shape `RouterHistory` hands its subscribers.
 */
function toHistoryLocation(href: string, rawState: unknown): HistoryLocation {
  const hashIndex = href.indexOf('#')
  const searchIndex = href.indexOf('?')
  const pathEnd =
    hashIndex > 0
      ? searchIndex > 0
        ? Math.min(hashIndex, searchIndex)
        : hashIndex
      : searchIndex > 0
        ? searchIndex
        : href.length

  return {
    href,
    pathname: href.substring(0, pathEnd),
    hash: hashIndex > -1 ? href.substring(hashIndex) : '',
    search:
      searchIndex > -1 ? href.slice(searchIndex, hashIndex === -1 ? undefined : hashIndex) : '',
    state: isHistoryState(rawState) ? rawState : { __TSR_index: 0 },
  }
}

export interface LocaleAwareHistory {
  /** Hand this to `createRouter({ history })`. */
  history: RouterHistory
  /** The language the current URL is being served in. */
  getLocale: () => SupportedLangCode
  /**
   * Rewrites the current URL into another language, in place.
   *
   * The router's own path is unchanged, so the visitor stays exactly where
   * they were — same route, same params, same query — and only the locale
   * segment of the address bar moves.
   */
  setLocale: (locale: SupportedLangCode) => void
}

/**
 * Sends `/fr...` to its real French URL before the router ever sees it.
 *
 * French lives at the root, so `/fr/about` is an alias the app never emits.
 * The edge answers it with a permanent redirect (see `vercel.json`); this is
 * the in-app fallback for the case where the SPA is reached some other way.
 */
export function normalizeDefaultLocaleAlias(browserWindow: Window): void {
  const target = redirectedDefaultLocalePath(browserWindow.location.pathname)
  if (!target) return
  browserWindow.history.replaceState(
    browserWindow.history.state,
    '',
    `${target}${browserWindow.location.search}${browserWindow.location.hash}`,
  )
}

export function createLocaleAwareHistory(browserWindow: Window): LocaleAwareHistory {
  normalizeDefaultLocaleAlias(browserWindow)

  let activeLocale: SupportedLangCode = parseLocalePath(browserWindow.location.pathname).locale

  const history = createBrowserHistory({
    window: browserWindow,
    // Called on load and on every popstate, so back/forward across a language
    // boundary re-syncs the active locale rather than stranding the app on
    // the previous one.
    parseLocation: () => {
      const parsed = parseLocalePath(browserWindow.location.pathname)
      activeLocale = parsed.locale
      return toHistoryLocation(
        `${parsed.path}${browserWindow.location.search}${browserWindow.location.hash}`,
        browserWindow.history.state,
      )
    },
    createHref: (href) => localizedHref(href, activeLocale),
  })

  return {
    history,
    getLocale: () => activeLocale,
    setLocale: (locale) => {
      if (locale === activeLocale) return
      const { pathname, search, hash, state } = history.location
      const internalHref = `${pathname}${search}${hash}`
      // Nothing to rewrite on an unlocalized path (the admin back-office):
      // its href is identical in every language.
      if (localizedHref(internalHref, locale) === localizedHref(internalHref, activeLocale)) {
        activeLocale = locale
        return
      }
      activeLocale = locale
      history.replace(internalHref, state)
    },
  }
}

/**
 * The language the very first render must use, or `undefined` to let
 * i18next's own detector decide.
 *
 * Returning a concrete value here — rather than letting the detector run and
 * correcting it afterwards — is what stops the app flashing the persisted
 * language before the router reports the path: i18next is initialised with
 * `lng` already set, so no detection ever happens on a localized URL.
 */
export function initialLanguageFromLocation(
  browserWindow: Window | undefined,
): SupportedLangCode | undefined {
  if (!browserWindow) return undefined
  const pathname =
    redirectedDefaultLocalePath(browserWindow.location.pathname) ?? browserWindow.location.pathname
  const parsed = parseLocalePath(pathname)
  // The admin back-office is not locale-addressed, so the visitor's own
  // persisted preference stays in charge there.
  if (!isLocalizablePath(parsed.path)) return undefined
  return parsed.hasLocalePrefix ? parsed.locale : DEFAULT_LOCALE
}
